import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { FiChevronDown } from "react-icons/fi";
import { COUNTRIES, byDial } from "./countries";

/**
 * One control for "country code + local number": a flag / dial-code picker joined to the number input.
 * `dial` and `number` are controlled by the parent.
 */
export default function PhoneField({ id, dial, number, onDial, onNumber, onBlur, invalid, describedBy }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(!byDial(dial) && !!dial);
  const [active, setActive] = useState(0);
  const root = useRef(null);
  const current = byDial(dial);

  const items = useMemo(() => [...COUNTRIES, { name: "Other / not listed", code: null, dial: "other" }], []);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choose = (item) => {
    setOpen(false);
    if (item.dial === "other") {
      setCustom(true);
      onDial("+");
    } else {
      setCustom(false);
      onDial(item.dial);
    }
  };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
      setActive(Math.max(0, items.findIndex((i) => i.dial === dial)));
      return;
    }
    if (!open) return;
    if (e.key === "Escape") setOpen(false);
    else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(items[active]);
    }
  };

  return (
    <div className={`phone-field ${invalid ? "is-invalid" : ""}`} ref={root}>
      <div className="phone-prefix">
        {custom ? (
          <input
            className="phone-custom"
            value={dial}
            onChange={(e) => onDial(e.target.value.replace(/[^\d+]/g, ""))}
            placeholder="+000"
            aria-label="Country code"
            inputMode="tel"
          />
        ) : (
          <button
            type="button"
            className="phone-trigger"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={`Country code ${current ? `${current.name} ${current.dial}` : dial}`}
            onClick={() => setOpen((o) => !o)}
            onKeyDown={onKey}
          >
            {current && <ReactCountryFlag countryCode={current.code} svg style={{ width: "1.35em", height: "1.35em" }} />}
            <span>{dial}</span>
            <FiChevronDown aria-hidden="true" />
          </button>
        )}

        {open && (
          <ul className="phone-list" role="listbox" aria-label="Country codes">
            {items.map((it, i) => (
              <li
                key={it.name}
                role="option"
                aria-selected={it.dial === dial}
                className={`${i === active ? "is-active" : ""} ${it.dial === dial ? "is-selected" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(it);
                }}
              >
                {it.code ? <ReactCountryFlag countryCode={it.code} svg style={{ width: "1.35em", height: "1.35em" }} /> : <span className="phone-flag-gap" />}
                <span className="phone-list-name">{it.name}</span>
                {it.code && <span className="phone-list-dial">{it.dial}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <input
        id={id}
        className="phone-number"
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={number}
        onChange={(e) => onNumber(e.target.value)}
        onBlur={onBlur}
        placeholder="Local number"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
    </div>
  );
}
